import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Chapter, REVEAL } from "./sections";
import { BookingForm } from "./booking/BookingForm";
import { PaymentStep } from "./booking/PaymentStep";
import { useAvailability } from "./booking/useAvailability";
import { usePaymentFlow } from "./booking/usePaymentFlow";

const EMPTY_FORM = {
  nombre: "", telefono: "", correo: "", servicio: "",
  fecha_preferida: "", horario_preferido: "", comentarios: "",
};

const REQUIRED = ["nombre", "telefono", "correo", "servicio", "fecha_preferida", "horario_preferido"];
const VIEW_30 = { once: true, amount: 0.3 };
const VIEW_20 = { once: true, amount: 0.2 };

export default function BookingSection({ selectedService = "", onServiceChange }) {
  const [form, setForm] = useState(EMPTY_FORM);
  const availability = useAvailability(form.fecha_preferida);

  const resetForm = () => {
    setForm(EMPTY_FORM);
    onServiceChange?.("");
  };

  const pay = usePaymentFlow({ form, onBooked: resetForm });

  useEffect(() => {
    setForm((f) => (f.servicio === selectedService ? f : { ...f, servicio: selectedService }));
  }, [selectedService]);

  const update = (k) => (e) => {
    const value = e.target.value;
    setForm((f) => ({ ...f, [k]: value }));
    if (k === "servicio") onServiceChange?.(value);
  };

  const onDateChange = (v) => {
    const valid = !v || availability.timesForDate(v).length > 0;
    if (!valid) toast.error("Esa fecha no está disponible. Elige otra, por favor.");
    setForm((f) => ({ ...f, fecha_preferida: valid ? v : "", horario_preferido: "" }));
  };

  const submit = (e) => {
    e.preventDefault();
    if (REQUIRED.some((k) => !form[k])) {
      toast.error("Por favor completa todos los campos obligatorios.");
      return;
    }
    pay.startCheckout();
  };

  return (
    <section id="agendar" className="mx-auto max-w-7xl px-6 py-24 lg:py-32 scroll-mt-20">
      <div className="grid lg:grid-cols-12 gap-12">
        <motion.div {...REVEAL} viewport={VIEW_30} className="lg:col-span-4">
          <Chapter num="03" label="Agendar cita" />
          <h2 className="font-display font-extrabold tracking-tighter leading-[0.95] text-4xl sm:text-5xl" style={{ color: "var(--text-dark)" }}>
            Reserva tu lugar
          </h2>
          <p className="mt-6 text-base leading-relaxed" style={{ color: "var(--text-muted)" }}>
            Completa el formulario y paga tu primera consulta ($200 MXN) con tarjeta, OXXO o PayPal para confirmar tu cita.
          </p>
          <div className="mt-8 overflow-hidden rounded-[1.75rem] aspect-[4/3] border-4 border-white shadow-xl">
            <img src="https://customer-assets-lxgj4vgw.emergentagent.net/job_dental-rebeca-mx/artifacts/0lzgmsd7_AdobeStock_100592748.webp"
              alt="Revisión dental" className="h-full w-full object-cover" />
          </div>
        </motion.div>

        <motion.div {...REVEAL} viewport={VIEW_20}
          className="lg:col-span-8 rounded-[2rem] border border-[var(--border)] bg-white p-6 sm:p-10 shadow-sm">
          {pay.step !== "form" && (
            <PaymentStep step={pay.step} oxxoHold={pay.oxxoHold} paypalClientId={pay.paypalClientId} form={form}
              onCancel={pay.cancelPayment} onPaypalApproved={pay.completeBooking} />
          )}
          <BookingForm form={form} update={update} onDateChange={onDateChange} availability={availability}
            loading={pay.loading} onSubmit={submit} visible={pay.step === "form"} />
        </motion.div>
      </div>
    </section>
  );
}
